import { ClockRotateRight, OpenInWindow } from 'iconoir-react'
import { observer } from 'mobx-react-lite'
import { ReactElement } from 'react'
import { LeadStatusCell } from 'app/components/contacts/LeadStatusCell'
import { ListMembershipRow } from 'app/components/contacts/ListMembershipRow'
import { PipelineStageDropdown } from 'app/components/contacts/PipelineStageDropdown'
import { StatusHistory } from 'app/components/contacts/StatusHistory'
import { type RuleListRow, useRuleListMembership } from 'app/components/contacts/useRuleListMembership'
import { Button } from 'app/components/ui/button'
import { useCheckPermission } from 'app/hooks/useCheckPermission'
import { useStatusListMemberships } from 'app/hooks/useStatusListMemberships'
import { closeModal, showModal } from 'app/lib/modal'
import type { ContactJson } from 'app/types/entities'
import type { StatusListRow } from 'app/types/list-memberships'
import { listPath } from 'lib/systemLists'

interface RuleListRowsProps {
  contact: ContactJson
  showHistory?: boolean
  onContactChange?: (contact: ContactJson) => void
}

interface RuleListRowProps {
  row: RuleListRow
  contact: ContactJson
  showHistory?: boolean
  statusRow?: StatusListRow
  readOnlyStatus?: string
  canEditLeadStatus: boolean
  onChangeStatus: (listId: string, stage: string) => void
  onContactChange?: (contact: ContactJson) => void
}

function RuleRow({
  row,
  contact,
  showHistory,
  statusRow,
  readOnlyStatus,
  canEditLeadStatus,
  onChangeStatus,
  onContactChange,
}: RuleListRowProps): ReactElement {
  const { def, band } = row
  const field = def.statusField
  // On the list with no status recorded yet: the contact sits in the list's default stage until someone moves them.
  const currentStage = statusRow?.stage ?? (field && contact[field] ? undefined : statusRow?.defaultStage)

  const stage =
    statusRow &&
    currentStage &&
    currentStage !== statusRow.catalogEntry.lockedStage &&
    statusRow.catalogEntry.stages.length > 0 ? (
      <PipelineStageDropdown
        stages={statusRow.catalogEntry.stages.filter((stage) => stage.name !== statusRow.catalogEntry.lockedStage)}
        currentStage={currentStage}
        onChange={(next) => onChangeStatus(statusRow.listId, next)}
      />
    ) : field === `leadStatus` ? (
      <LeadStatusCell contact={contact} editable={canEditLeadStatus} variant="field" onSaved={onContactChange} />
    ) : (
      // A stage only a booking sets (Scheduled) reads as text, never a picker. So does a status list whose
      // record has not loaded.
      <span className="shrink-0 text-body-xs text-subtle">{statusRow ? currentStage : readOnlyStatus}</span>
    )

  return (
    <ListMembershipRow
      id={def.slug}
      name={def.name}
      detail={band?.label}
      stage={stage}
      actions={
        <>
          {showHistory && field && contact.id && (
            <Button
              variant="icon"
              aria-label={`${def.name} history`}
              className="size-5"
              onClick={() =>
                showModal(<StatusHistory contactId={contact.id!} field={field} />, {
                  useRadix: true,
                  isFullScreen: false,
                  title: `${def.name} history`,
                  panelClassName: `min-w-0 sm:max-w-md`,
                  onClose: closeModal,
                })
              }
            >
              <ClockRotateRight className="size-4 text-minimal" />
            </Button>
          )}
          <a
            href={listPath(def.slug)}
            aria-label={`Open ${def.name}`}
            className="size-5 flex items-center justify-center"
          >
            <OpenInWindow className="size-4 text-minimal" />
          </a>
        </>
      }
    />
  )
}

/**
 * The rule lists a contact currently qualifies for, above the lists somebody put them on.
 *
 * No remove action anywhere in the group: leaving a rule list means changing the fact the
 * rule reads, not deleting a membership.
 */
export const RuleListRows = observer(function RuleListRows({
  contact,
  showHistory,
  onContactChange,
}: RuleListRowsProps): ReactElement | null {
  const { rows } = useRuleListMembership(contact)
  const status = useStatusListMemberships(contact)
  const checkPermission = useCheckPermission()
  if (rows.length === 0) return null

  const canEditLeadStatus = checkPermission([`org:contacts:edit-lead-status`, `contacts:lead-status`])

  return (
    <div className="flex w-full flex-col gap-1">
      {rows.map((row) => (
        <RuleRow
          key={row.def.slug}
          contact={contact}
          showHistory={showHistory}
          row={row}
          statusRow={status.rows.find((statusRow) => statusRow.def.slug === row.def.slug)}
          readOnlyStatus={(row.def.statusField ? contact[row.def.statusField] : contact.leadStatus) ?? undefined}
          canEditLeadStatus={canEditLeadStatus}
          onChangeStatus={status.changeStage}
          onContactChange={onContactChange}
        />
      ))}
    </div>
  )
})
